import { useEffect, useRef, type ReactNode, type ElementType } from "react";

/**
 * Lightweight scroll-reveal wrapper.
 * - Adds .is-visible to .km-reveal when the element enters the viewport.
 * - Honors prefers-reduced-motion via CSS (.km-reveal rules in styles.css).
 * - No-ops when IntersectionObserver is unavailable (server / very old browsers).
 */
export function ScrollReveal({
  as: Tag = "div",
  children,
  className = "",
  delayMs = 0,
}: {
  as?: ElementType;
  children: ReactNode;
  className?: string;
  delayMs?: number;
}) {
  const ref = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      if (el) el.classList.add("is-visible");
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const target = entry.target as HTMLElement;
            if (delayMs > 0) {
              window.setTimeout(() => target.classList.add("is-visible"), delayMs);
            } else {
              target.classList.add("is-visible");
            }
            obs.unobserve(target);
          }
        }
      },
      { rootMargin: "0px 0px -10% 0px", threshold: 0.05 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [delayMs]);

  const Component = Tag as ElementType;
  return (
    <Component ref={ref as never} className={`km-reveal ${className}`.trim()}>
      {children}
    </Component>
  );
}
